import {
  useGetCallbackSapInfo,
  useSendCallbackSap,
} from "hooks/callbackSap/useCallbackSap";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { numberWithCommas } from "helpers/utils";

/**
 * Chuẩn hóa response của API callback-sap-info.
 *
 * Shape BE trả về (phần `data` bên trong { result, data }):
 * {
 *   can_callback_sap: true,
 *   replacement: { id, serial, number, buyer_name, buyer_tax, total },
 *   original: { id, ref_no },
 *   default_refno: "100020269005512402-1134"
 * }
 */
export const normalizeCallbackSapInfo = data => {
  if (!data) {
    return null;
  }

  const replacement = data.replacement || {};
  const original = data.original || {};

  return {
    // BE chặn ở đây một lần nữa dù list đã ẩn action theo can_callback_sap
    canCallback: data.can_callback_sap !== false,
    replacementId: replacement.id,
    invoiceSymbol: replacement.serial,
    invoiceNumber: replacement.number,
    buyerName: replacement.buyer_name,
    buyerTax: replacement.buyer_tax,
    totalAmount: replacement.total,
    originalId: original.id,
    originalRefno: original.ref_no,
    defaultRefno: data.default_refno || original.ref_no || "",
  };
};

const CallbackSapModal = props => {
  const { isOpen, toggle, row, onSuccess } = props;

  const [info, setInfo] = useState(null);
  const [refno, setRefno] = useState("");
  const [errorRefno, setErrorRefno] = useState(null);

  const getCallbackSapInfo = useGetCallbackSapInfo({
    onSuccess: ({ data }) => {
      const normalized = normalizeCallbackSapInfo(data);
      setInfo(normalized);
      setRefno(normalized?.defaultRefno || "");
    },
  });

  const sendCallbackSap = useSendCallbackSap({
    onSuccess: () => {
      toggle();
      if (typeof onSuccess === "function") {
        onSuccess();
      }
    },
  });

  const loadInfo = () => {
    if (!row?.id) {
      return;
    }
    // `row` chỉ dùng cho mock, API thật chỉ cần id
    getCallbackSapInfo.mutate({ id: row.id, row });
  };

  useEffect(() => {
    if (isOpen) {
      loadInfo();
    } else {
      setInfo(null);
      setRefno("");
      setErrorRefno(null);
      getCallbackSapInfo.reset();
      sendCallbackSap.reset();
    }
  }, [isOpen]);

  const handleChangeRefno = e => {
    setRefno(e.target.value);
    if (errorRefno) {
      setErrorRefno(null);
    }
  };

  const handleSend = () => {
    const value = (refno || "").trim();
    if (!value) {
      setErrorRefno("Vui lòng nhập mã tham chiếu (refno)");
      return;
    }
    sendCallbackSap.mutate({ id: row?.id, refno: value });
  };

  const renderInfoRow = (label, value) => (
    <Row className="mb-2">
      <Col xs="5" className="text-muted">
        {label}
      </Col>
      <Col xs="7">{value}</Col>
    </Row>
  );

  const renderBody = () => {
    if (getCallbackSapInfo.isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-4">
          <Spinner color="primary" />
        </div>
      );
    }

    if (getCallbackSapInfo.isError || !info) {
      return (
        <div className="text-center py-4">
          <div className="text-danger mb-3">
            Không lấy được thông tin hóa đơn để gửi callback
          </div>
          <Button type="button" color="primary" outline onClick={loadInfo}>
            <i className="bx bx-refresh me-1" />
            Thử lại
          </Button>
        </div>
      );
    }

    return (
      <React.Fragment>
        <div className="bg-light rounded p-3 mb-3">
          {renderInfoRow(
            "Hóa đơn thay thế",
            <span className="text-primary">
              {[info.invoiceNumber, info.invoiceSymbol]
                .filter(Boolean)
                .join(" - ") || info.replacementId}
            </span>
          )}
          {renderInfoRow(
            "Khách hàng",
            <span className="text-primary">{info.buyerName}</span>
          )}
          {renderInfoRow("MST", info.buyerTax)}
          {renderInfoRow("Tổng tiền", numberWithCommas(info.totalAmount))}
          <hr className="my-3" />
          {renderInfoRow("Hóa đơn gốc", info.originalId)}
          {renderInfoRow("Số tham chiếu gốc", info.originalRefno)}
        </div>

        {!info.canCallback && (
          <Alert color="warning" className="mb-3 font-size-13">
            Hóa đơn này hiện không đủ điều kiện gửi callback về SAP.
          </Alert>
        )}

        <Label className="mb-1">
          Mã tham chiếu (refno) <span className="text-danger">*</span>
        </Label>
        <Input
          type="text"
          value={refno}
          invalid={!!errorRefno}
          disabled={sendCallbackSap.isLoading || !info.canCallback}
          onChange={handleChangeRefno}
        />
        <FormFeedback>{errorRefno}</FormFeedback>
        <div className="text-muted font-size-12 mt-1">
          Định dạng: 18 chữ số - 4 chữ số mã đơn vị. Nên dùng refno của hóa đơn
          gốc.
        </div>

        <Alert color="info" className="mt-3 mb-0 font-size-13">
          Sau khi gửi, hệ thống sẽ tự động gọi API callback cho PNJ tương ứng
          với mã refno này. Tiến trình chạy ngầm, bạn không cần chờ trên màn
          hình.
        </Alert>
      </React.Fragment>
    );
  };

  return (
    <Modal
      contentClassName="confirm-modal"
      isOpen={isOpen}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalHeader toggle={toggle}>
          <Label className="mb-0">
            <i className="bx bx-refresh text-primary me-1" />
            Gửi callback về SAP
          </Label>
        </ModalHeader>
        <ModalBody>{renderBody()}</ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="light"
            disabled={sendCallbackSap.isLoading}
            onClick={toggle}
          >
            Hủy
          </Button>
          <Button
            type="button"
            color="primary"
            disabled={
              !info ||
              !info.canCallback ||
              getCallbackSapInfo.isLoading ||
              sendCallbackSap.isLoading
            }
            onClick={handleSend}
          >
            {sendCallbackSap.isLoading && (
              <Spinner size="sm" className="me-1" />
            )}
            Gửi callback
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
};

CallbackSapModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  row: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default CallbackSapModal;
